import type { ClawMessage } from '../types';

const isAsyncTaskMessage = (msg: ClawMessage) => msg.role === 'cron' || msg.role === 'spawn' || !!msg.task_id;

// 一条消息是否是 assistant 的"最终产出"：文本正文（type=text + status!=analysis 且有内容）
// 或任意 artifact.created 事件（AI 只发文件不发文本的场景）。
const isFinalAssistantOutput = (msg: ClawMessage): boolean => {
  if (msg.event_type === 'conversation.artifact.created') return true;
  if (msg.role !== 'assistant') return false;
  return (msg.contents ?? []).some(
    (content) => content.type === 'text' && content.status !== 'analysis' && !!content.content?.trim(),
  );
};

// 历史按 DESC 排序（最新在前）。判定"最近一个 turn 是否仍在跑"：
// - 找到第一条非异步任务、带 turn_id 的消息，确定 candidate turnId；
// - candidate 之后（更新的那一侧）若已出现"无 turn_id 的 assistant 最终产出"，说明这是 coordinator
//   会话收尾时下发的汇报/产物（挂在 turn_id="" 上），整条协调流已结束，视为已收尾。
//   只看比 candidate 更新的消息，避免历史里更早一次协调收尾误杀当前正在跑的新 turn。
// - 否则在该 turn 内若已有"最终 assistant 产出"则视为已收尾；都没有则视为仍在跑。
// 异步消息（cron/spawn）后端会挂在当前进行中的 turn 上，不能拿来判定 turn 归属。
export const inferRunningTurnId = (records: ClawMessage[]): string | null => {
  const candidateIndex = records.findIndex((msg) => !!msg.turn_id && !isAsyncTaskMessage(msg));
  if (candidateIndex < 0) return null;
  const turnId = records[candidateIndex].turn_id;
  if (!turnId) return null;

  // coordinator session 结束信号：candidate 更新一侧存在"无 turn_id 的 assistant 最终产出"。
  const hasCoordinatorFinalOutput = records
    .slice(0, candidateIndex)
    .some((msg) => !isAsyncTaskMessage(msg) && !msg.turn_id && isFinalAssistantOutput(msg));
  if (hasCoordinatorFinalOutput) return null;

  // 该 turn 内已有 assistant 最终产出 → 视为已收尾。
  const hasFinalAssistant = records.some((msg) => msg.turn_id === turnId && isFinalAssistantOutput(msg));

  return hasFinalAssistant ? null : turnId;
};

// 历史 DESC 下最新一条非异步消息若是 user，说明已发问但 assistant 尚未写入历史。
export const isAwaitingAssistantReply = (records: ClawMessage[]): boolean => {
  const newest = records.find((msg) => !isAsyncTaskMessage(msg));
  return newest?.role === 'user';
};

// user 消息的 stream_seq 恒为 "0"，不能用于 SSE 断点续传；取非 user 消息中的最大 seq。
export const getLatestStreamSeqFromHistory = (records: ClawMessage[]): string | null => {
  let maxSeq: number | null = null;

  for (const msg of records) {
    if (msg.role === 'user') continue;
    const seq = msg.stream_seq;
    if (!seq) continue;

    const parsed = Number(seq);
    if (!Number.isFinite(parsed) || parsed <= 0) continue;

    if (maxSeq === null || parsed > maxSeq) {
      maxSeq = parsed;
    }
  }

  return maxSeq === null ? null : String(maxSeq);
};
